define(['./config.js', './util.js'], function(config, util) {
    'use strict';

    var singleMustacheRegex = new RegExp('\\{\\{\\_\\s*(.*?)\\s*\\_\\}\\}');
    var multiMustacheRegex = new RegExp('\\{\\{\\_\\s*(.*?)\\s*\\_\\}\\}', 'g');
    var exactlyOneMustacheRegex = new RegExp('^\\{\\{\\_\\s*([^}]*?)\\s*\\_\\}\\}$');
    var baseRegex = '\\((.*?)\\)(?:\\.([^+\\-*\\/\\s|()]*))?(?:\\|(number|date)(?:\\(([^)]*)\\))?)?';
    var regexes = {
        settings: new RegExp('settings' + baseRegex, 'g'),
        model: new RegExp('model' + baseRegex, 'g'),
        labels: new RegExp('labels' + baseRegex, 'g'),
        parameters: new RegExp('parameters' + baseRegex, 'g'),
        value: new RegExp('value' + baseRegex, 'g')
    };

    function ConfigEvaluator(modulePath, instanceRegistry) {
        this.modulePath = modulePath;
        this.instanceRegistry = instanceRegistry;
    }

    ConfigEvaluator.prototype.evaluate = function evaluate(obj, evaluationTypes) {
        this.references = [];
        this.evaluationTypes = evaluationTypes || {
                settings: true,
                model: true,
                labels: true,
                parameters: true,
                value: true
            };
        this.obj = obj;
        var evaluated = this._traverse(obj);

        return {
            evaluated: evaluated,
            references: this.references
        };
    };

    ConfigEvaluator.prototype._hasMustache = function _hasMustache(str) {
        return singleMustacheRegex.test(str);
    };

    ConfigEvaluator.prototype._evaluateString = function _evaluateString(str, path) {
        var self = this;
        if (!this._hasMustache(str)) {
            return str;
        }

        var source = {
            expression: str,
            path: path
        };
        var result = null;
        if (exactlyOneMustacheRegex.test(str)) {
            // exactly one match (this may return other types than string)
            var match = str.match(exactlyOneMustacheRegex);
            result = self._evaluateStringExpressions(match[1], match[0], source);
        } else {
            // multiple mustaches/string composition (will always return a string)
            result = str.replace(multiMustacheRegex, function(whole, inner) {
                return self._evaluateStringExpressions(inner, whole, source);
            });
        }

        return result;
    };

    ConfigEvaluator.prototype._evaluateStringExpressions = function _evaluateStringExpressions(str, mustache, source) {
        this.hasStringValue = false;
        var original = str;
        var self = this;

        util.each(this.evaluationTypes, function(type, key) {
            str = self._evaluateStringExpressionType(str, key, source, mustache, function(instance) {
                return instance['get' + util.string.firstUpperCase(key)]();
            });
        });

        return str === original ? mustache : (!this.hasStringValue ? this._eval(str) : str);
    };

    ConfigEvaluator.prototype._eval = function _eval(str) {
        return this._isJsonSerialized(str) ? JSON.parse(str) : eval(str);
    };

    ConfigEvaluator.prototype._isJsonSerialized = function _isJsonSerialized(str) {
        return str.indexOf('{') === 0 || str.indexOf('[') === 0;
    };

    ConfigEvaluator.prototype._evaluateStringExpressionType = function _evaluateStringExpressionType(str, type, source, mustache, instanceCallback) {
        var self = this;
        str = str.replace(regexes[type], function(match, relativeModulePath, propsExpression, formatType, format) {
            return self._getReplacementsRecursively(instanceCallback, relativeModulePath, propsExpression, formatType, format, type, source, mustache);
        });

        return str;
    };

    ConfigEvaluator.prototype._getReplacementsRecursively = function _getReplacementsRecursively(instanceCallback, relativeModulePath, propsExpression,
                                                                                                 formatType, format, type, source, mustache) {
        var referencedInstance;
        try {
            referencedInstance = this.instanceRegistry.get(this.modulePath, relativeModulePath);
        } catch (ex) {
            this.hasStringValue = true;
            util.log.error('ConfigEvaluator failed to evaluate expression', mustache, 'from component', this.modulePath);
            return mustache;
        }

        var self = this;

        var value;
        if (!referencedInstance) {
            throw new Error('ConfigEvaluator could not find referenced instance: ' + this.modulePath + ', ' +
                relativeModulePath);
        }
        var originalReference = {
            type: type,
            instance: referencedInstance,
            refPath: propsExpression || '',
            source: source,
            listen: this._createListener(type, propsExpression || '', referencedInstance),
            assign: this._createAssigner(type, propsExpression || '', referencedInstance),
            eventType: this._getEventType(type, propsExpression || ''),
            mustache: mustache,
            modulePath: this.modulePath,
            relativeModulePath: relativeModulePath,
            formatType: formatType,
            format: format
        };
        value = instanceCallback(referencedInstance);
        value = propsExpression ? util.parse(propsExpression)(value) : value;

        if (this._hasMustache(value) && type !== 'model') {
            // returned value has a mustache
            // in the returned references, we skip straight to the end of the "mustache chain"
            var relative = util.componentPath.relative(self.modulePath, referencedInstance.getModulePath());
            var evaluator = new ConfigEvaluator(referencedInstance.getModulePath(), this.instanceRegistry);
            var result = evaluator.evaluate(value);
            util.each(result.references, function(v) {
                // replace modulePath, source and source expression for the reference and base relative module paths from self.modulePath
                var relativeModulePath = !relative ? v.relativeModulePath :
                    util.componentPath.resolve(relative, v.relativeModulePath);
                var replacementMustache = v.mustache.replace('(' + v.relativeModulePath + ')', '(' +
                    relativeModulePath + ')');
                if (formatType) {
                    // add the calling end of the chain's format to the mustache
                    var formatString = '|' + formatType + (format ? '(' + format + ')' : '');
                    replacementMustache = replacementMustache.replace(/(?:\|.*?)?(\s*\_\}\})/, function(match, end) {
                        return formatString + end;
                    });
                }

                v.modulePath = self.modulePath;
                v.source = originalReference.source;
                v.source.expression = v.source.expression.replace(mustache, replacementMustache);
            });
            this.references = this.references.concat(result.references);
            value = result.evaluated;
        } else {
            // no mustache in the returned value -- use original reference
            this.references.push(originalReference);
        }

        originalReference.value = value;
        value = this._getFormatted(value, this.instanceRegistry.get(this.modulePath), formatType, format);
        return this.hasStringValue === true ? value : JSON.stringify(value);
    };

    ConfigEvaluator.prototype._getEventType = function _getEventType(type, refPath) {
        switch (type) {
            case 'parameters':
                if (refPath) {
                    return 'parameterChanged:' + refPath;
                } else {
                    return 'parametersChanged';
                }
            case 'model':
                if (refPath) {
                    return 'modelPropertyChanged:' + refPath;
                } else {
                    return 'modelChanged';
                }
            case 'value':
                if (refPath) {
                    //throw new Error('refPath for reference of type "value" in ConfigEvaluator is not implemented yet');
                    return 'valuePropertyChanged:' + refPath;
                } else {
                    return 'valueChanged';
                }
        }

        return null;
    };

    ConfigEvaluator.prototype._createAssigner = function _createAssigner(type, refPath, instance) {
        return function assign(value) {
            switch (type) {
                case 'parameters':
                    if (!refPath) {
                        instance.setParameters(value);
                    } else {
                        instance.setParameter(refPath, value);
                    }
                    break;
                case 'model':
                    if (!refPath) {
                        instance.setModel(value);
                    } else {
                        instance.setModelProperty(refPath, value);
                    }
                    break;
            }
        };
    };

    ConfigEvaluator.prototype._createListener = function _createListener(type, refPath, instance) {
        var self = this;
        return function(onChangeCallback) {
            var listenerType = self._getEventType(type, refPath);
            if (listenerType == null) {
                return util.noop;
            }

            return instance.on(listenerType, onChangeCallback);
        };
    };

    ConfigEvaluator.prototype._getFormatted = function _getFormatted(value, instance, formatType, format) {
        if (formatType == null) {
            if (util.isString(value)) {
                this.hasStringValue = true;
            }

            return value;
        }

        this.hasStringValue = true;
        var formatter = instance.getFormatter();
        if (formatType === 'number') {
            value = formatter.number(value, {
                formatString: format
            });
        } else if (formatType === 'date') {
            value = formatter.date(value, {
                formatString: format
            });
        }

        return value;
    };

    ConfigEvaluator.prototype._traverse = function _traverse(obj, path) {
        path = path || '';
        var self = this;
        if (util.isString(obj)) {
            return self._evaluateString(obj, path);
        } else if (util.isPlainObject(obj) || util.isArray(obj)) {
            var originalPath = path;
            util.each(obj, function(value, prop) {
                path = originalPath ? (originalPath + '.' + prop) : prop;
                obj[prop] = self._traverse(value, path);
            });
        }

        return obj;
    };

    return ConfigEvaluator;
});



/*****************
 ** WEBPACK FOOTER
 ** ./components/core/src/js/config-evaluator.js
 ** module id = 120
 ** module chunks = 0
 **/